/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Wednesday, September 14th 2022, 5:36:46 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { UseFormReturn } from "react-hook-form";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { DataMapSourceMetadata, DatasetSchema, FormField, FormConfiguration, FIELD_TYPES } from "lib/schemas";
import { ListEditorProvider, ListEditorContext, useListEditorContext } from "lib/providers";
import { FormPicker, FieldPicker } from "form-builder/components";
import { OSAlert, OSActionButton, HelperText, OSDropDown } from "ui-widgets";

type ListedDataMapSourceMetadata = DataMapSourceMetadata & { dataset_field_key: string };

type TOnChangeDataMappingFn = (targetFieldschemaFieldKey: string, data_map_metadata: DataMapSourceMetadata) => void;

interface DatasetReferenceUpdateSettingsProps {
  referencedDatasetSchema: DatasetSchema;
  setReferencedDatasetSchema: (datasetSchema: DatasetSchema) => void;
  useFormReturn: UseFormReturn;
}

export const DatasetReferenceUpdateSettings = ({
  referencedDatasetSchema,
  setReferencedDatasetSchema,
  useFormReturn: { setValue, watch },
}: DatasetReferenceUpdateSettingsProps) => {
  const ref_metadata = watch("ref_metadata");

  const referencedDatasetFieldSchemas = referencedDatasetSchema?.json_schema?.properties;

  const dataMaps = Object.keys(ref_metadata?.data_mapping ?? {}).map((dataset_field_key) => ({
    dataset_field_key: dataset_field_key,
    ...ref_metadata?.data_mapping[dataset_field_key],
  }));

  const [sourceFormId, setSourceFormId] = React.useState<number>();

  const onChangeDataMapping = (targetFieldschemaFieldKey: string, data_map_metadata: ListedDataMapSourceMetadata) => {
    const { source_field_guid, source_field_data_key } = data_map_metadata;
    setValue("ref_metadata", {
      ...ref_metadata,
      data_mapping: {
        ...ref_metadata.data_mapping,
        [targetFieldschemaFieldKey]: { source_field_guid, source_field_data_key },
      },
    });
  };

  return (
    <>
      <Stack spacing={2}>
        <Typography variant="h6" className="os-content-description">
          Dataset Update Settings
        </Typography>
        <HelperText>Choose a form to filter fields where data will be pulled from</HelperText>
        <FormPicker
          handleChooseForm={(form) => setSourceFormId(form?.id)}
          formId={null}
          label="Filter source fields by Form"
        />
        <ListEditorProvider<ListedDataMapSourceMetadata, false>
          uniqueIdentifier="dataset_field_key"
          apiServicePath={null}
          initialItems={dataMaps}
        >
          <ListEditorContext.Consumer>
            {({ itemsListState: { savedItems, itemCreationQueue }, createNewItem, selectItem }) => (
              <>
                {savedItems.concat(itemCreationQueue).map((dataMap) => (
                  <div onClick={() => selectItem(dataMap.dataset_field_key)}>
                    <DataMapConfiguration
                      referencedDatasetFieldSchemas={referencedDatasetFieldSchemas}
                      dataMap={dataMap}
                      sourceFormId={sourceFormId}
                      onChangeDataMapping={onChangeDataMapping}
                    />
                  </div>
                ))}
                <Stack direction="row" spacing={2}>
                  <OSActionButton
                    actionStyle="add"
                    buttonType="floating"
                    onClick={() => createNewItem({ source_field_guid: null })}
                  />
                  <Typography variant="caption" className="os-content-description">
                    Add mapping for a target dataset field from form field
                  </Typography>
                </Stack>
              </>
            )}
          </ListEditorContext.Consumer>
        </ListEditorProvider>
      </Stack>
    </>
  );
};

interface DataMapConfigurationProps {
  onChangeDataMapping: TOnChangeDataMappingFn;
  referencedDatasetFieldSchemas: DatasetSchema["json_schema"];
  dataMap: ListedDataMapSourceMetadata;
  sourceFormId: FormConfiguration["id"];
}
const DataMapConfiguration = ({
  referencedDatasetFieldSchemas,
  dataMap,
  sourceFormId,
  onChangeDataMapping,
}: DataMapConfigurationProps) => {
  const [targetFieldschemaFieldKey, setTargetFieldschemaFieldKey] = React.useState(dataMap?.dataset_field_key);
  const [sourceField, setSourceField] = React.useState<FormField>();

  const {
    itemsListState: { savedItems },
    dequeueNewItem,
  } = useListEditorContext<ListedDataMapSourceMetadata>();

  const targetFieldDataMap = savedItems.find((dataMap) => dataMap.dataset_field_key === targetFieldschemaFieldKey);

  const handleSaveDataMap = (dataMapToSave: DataMapSourceMetadata) => {
    onChangeDataMapping(targetFieldschemaFieldKey, dataMapToSave);
    dequeueNewItem((dataMap as any).os_unsaved_item_temp_unique_id);
  };

  const handleChooseTargetField = (dataset_selected_field_key: ListedDataMapSourceMetadata["dataset_field_key"]) => {
    setTargetFieldschemaFieldKey(dataset_selected_field_key);
  };

  const handleChooseSourceField = (field: FormField) => {
    if (!field) {
      setSourceField(null);
      return;
    }

    handleSaveDataMap({ ...targetFieldDataMap, source_field_guid: field.artifact_guid });
    setSourceField(field);
  };

  const handleChooseSourceFieldDataKey = (source_field_data_key: string) => {
    handleSaveDataMap({
      ...targetFieldDataMap,
      source_field_data_key: source_field_data_key,
    });
  };

  const targetFieldOptions = Object.keys(referencedDatasetFieldSchemas).reduce(
    (built_options, current_dataset_field_key) => ({
      ...built_options,
      [current_dataset_field_key]: referencedDatasetFieldSchemas[current_dataset_field_key]?.title,
    }),
    {}
  );

  return (
    <Stack direction="row" spacing={2}>
      <div style={{ flexBasis: "50%" }}>
        <OSDropDown
          options={targetFieldOptions}
          onChangeCallBack={handleChooseTargetField}
          value={targetFieldschemaFieldKey}
          labelId="target-dataset-field"
          label="Target Field on Dataset"
        />
      </div>

      {targetFieldschemaFieldKey && (
        <div style={{ flexBasis: "50%" }}>
          {!sourceFormId && !dataMap?.source_field_guid ? (
            <OSAlert severity="info" variant="outlined">
              Choose form to filter fields.
            </OSAlert>
          ) : (
            <>
              <FieldPicker
                handleChooseField={handleChooseSourceField}
                onLoadIntial={handleChooseSourceField}
                fieldIdentifier={dataMap?.source_field_guid}
                label="Source Field on Form"
                formId={sourceFormId}
              />
              <SourceFieldDataKeySelector
                source_field_data_key={dataMap?.source_field_data_key}
                sourceField={sourceField}
                handleChooseSourceFieldDataKey={handleChooseSourceFieldDataKey}
              />
            </>
          )}
        </div>
      )}
    </Stack>
  );
};

interface SourceFieldDataKeySelectorProps {
  source_field_data_key: string;
  sourceField: FormField;
  handleChooseSourceFieldDataKey: (selected_source_field_data_key: string) => void;
}

const getDynamicMatrixKeyOptions = (field: FormField) => {
  return field.dynamic_matrix_settings.columns.reduce(
    (built_options, current_column) => ({ ...built_options, [current_column.name]: current_column.label }),
    {}
  );
};

const getGeoCoordinatedKeyOptions = () => {
  return { longitude: "Longitude", latitude: "Latitude" };
};

const SourceFieldDataKeySelector = ({
  source_field_data_key,
  sourceField,
  handleChooseSourceFieldDataKey,
}: SourceFieldDataKeySelectorProps) => {
  let sourceFieldDataKeyOptions: { [key: string]: string } = null;
  switch (sourceField?.field_type) {
    case FIELD_TYPES.GEO_COORDINATES:
      sourceFieldDataKeyOptions = getGeoCoordinatedKeyOptions();
      break;
    case FIELD_TYPES.DYNAMIC_MATRIX:
      sourceFieldDataKeyOptions = getDynamicMatrixKeyOptions(sourceField);
      break;
  }

  return (
    sourceFieldDataKeyOptions && (
      <OSDropDown
        options={sourceFieldDataKeyOptions}
        onChangeCallBack={handleChooseSourceFieldDataKey}
        value={source_field_data_key}
        labelId="source-field-data-key"
        label="Source Field Data Key"
      />
    )
  );
};
